import { ChangeEvent, FC, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import clsx from 'clsx';
import { isNotEmpty } from '../../../../../_metronic/helpers';
import { initialUser, User } from '../core/_models';
import { useListView } from '../core/ListViewProvider';
import { createUser, updateUser } from '../core/_requests';
import { useQueryResponse } from '../core/QueryResponseProvider';
import { FormattedMessage, useIntl } from 'react-intl';
import { UsersListLoading } from '../components/loading/UsersListLoading';
import axios from 'axios';
import { RolePermission } from '../../../role-permission/roles-permissions-list/core/_models';
import { Currency } from '../../../currency/currencies-list/core/_models';

type Props = {
  isUserLoading: boolean;
  user: User;
};

const editUserSchema = Yup.object().shape({
  name: Yup.string().required('First name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  phone: Yup.string().required('Phone number is required'),
  password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  confirm_password: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Confirm password is required'),
  roles: Yup.string().required('Role is required'),
});

const UserEditModalForm: FC<Props> = ({ user, isUserLoading }) => {
  const API_URL = process.env.REACT_APP_THEME_API_URL;
  const CURRENCY_URL = `${API_URL}/admin/currencies`;
  const intl = useIntl();
  const { setItemIdForUpdate } = useListView();
  const { refetch } = useQueryResponse();

  const [role, setRole] = useState<[RolePermission]>();
  const [currencies, setCurrencies] = useState<[Currency]>();

  const [userForEdit] = useState<User>({
    ...user,
    name: user.name || initialUser.name,
    email: user.email || initialUser.email,
    phone: user.phone || initialUser.phone,
    roles: user?.roles?.id || initialUser?.roles?.id,
    currency: user?.currency?.id || initialUser?.currency?.id,

  });


  useEffect(() => {
    //get roles
    axios
      .get(`${process.env.REACT_APP_THEME_API_URL}/admin/roles`)
      .then((response) => {
        console.log('Roles API Response:', response.data);
        const data = response.data
        if (data.success) {
          setRole(data.data.roles);
        }
      })
      .catch((error) => {
        console.error('Error fetching roles:', error);
      });
    //get companies
    axios
      .get(CURRENCY_URL)
      .then((response) => {
        const data = response.data;
        if (data.success) {
          setCurrencies(data.data.currencies);
        }
      })
      .catch((error) => {
        console.error(error);
      });
    
  }, []);

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch();
    }
    setItemIdForUpdate(undefined);
  };

  const formik = useFormik({
    initialValues: userForEdit,
    validationSchema: editUserSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        if (isNotEmpty(values.id)) {
          await updateUser(values);
        } else {
          await createUser(values);
        }
      } catch (ex) {
        console.error(ex);
      } finally {
        setSubmitting(false);
        cancel(true);
      }
    },
  });

  return (
    <>
      <form
        id="kt_modal_add_user_form"
        encType="multipart/form-data"
        className="form"
        onSubmit={formik.handleSubmit}
        noValidate
      >
        <div className="d-flex flex-column scroll-y me-n7 pe-7">
          <div className="row">
            <div className="col-lg-6 fv-row mb-7">
              <label className="required fw-bold fs-6 mb-2">
                <FormattedMessage id="USER.FORM.INPUT.FIRSTNAME" defaultMessage="First Name" />
              </label>
              <input
                placeholder="Enter first name"
                {...formik.getFieldProps('name')}
                className={clsx(
                  'form-control form-control-solid',
                  { 'is-invalid': formik.touched.name && formik.errors.name },
                  { 'is-valid': formik.touched.name && !formik.errors.name }
                )}
                type="text"
                disabled={formik.isSubmitting || isUserLoading}
              />
              {formik.touched.name && formik.errors.name && (
                <div className="fv-plugins-message-container">
                  <span role="alert">{formik.errors.name}</span>
                </div>
              )}
            </div>

          </div>

          <div className="row">
            <div className="col-lg-6 fv-row mb-7">
              <label className="required fw-bold fs-6 mb-2">
                <FormattedMessage id="USER.FORM.INPUT.EMAIL" defaultMessage="Email" />
              </label>
              <input
                placeholder="Enter email"
                {...formik.getFieldProps('email')}
                className={clsx(
                  'form-control form-control-solid',
                  { 'is-invalid': formik.touched.email && formik.errors.email },
                  { 'is-valid': formik.touched.email && !formik.errors.email }
                )}
                type="email"
                disabled={formik.isSubmitting || isUserLoading}
              />
              {formik.touched.email && formik.errors.email && (
                <div className="fv-plugins-message-container">
                  <span role="alert">{formik.errors.email}</span>
                </div>
              )}
            </div>

            <div className="col-lg-6 fv-row mb-7">
              <label className="required fw-bold fs-6 mb-2">
                <FormattedMessage id="USER.FORM.INPUT.PHONENUMBER" defaultMessage="Phone Number" />
              </label>
              <input
                placeholder="Enter phone number"
                {...formik.getFieldProps('phone')}
                className={clsx(
                  'form-control form-control-solid',
                  { 'is-invalid': formik.touched.phone && formik.errors.phone },
                  { 'is-valid': formik.touched.phone && !formik.errors.phone }
                )}
                type="text"
                disabled={formik.isSubmitting || isUserLoading}
              />
              {formik.touched.phone && formik.errors.phone && (
                <div className="fv-plugins-message-container">
                  <span role="alert">{formik.errors.phone}</span>
                </div>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-lg-6 fv-row mb-7">
              <label className="required fw-bold fs-6 mb-2">
                <FormattedMessage id="USER.FORM.INPUT.PASSWORD" defaultMessage="Password" />
              </label>
              <input
                placeholder="Enter password"
                {...formik.getFieldProps('password')}
                className={clsx(
                  'form-control form-control-solid',
                  { 'is-invalid': formik.touched.password && formik.errors.password },
                  { 'is-valid': formik.touched.password && !formik.errors.password }
                )}
                type="password"
                disabled={formik.isSubmitting || isUserLoading}
              />
              {formik.touched.password && formik.errors.password && (
                <div className="fv-plugins-message-container">
                  <span role="alert">{formik.errors.password}</span>
                </div>
              )}
            </div>

            <div className="col-lg-6 fv-row mb-7">
              <label className="required fw-bold fs-6 mb-2">
                <FormattedMessage id="USER.FORM.INPUT.CONFIRMPASSWORD" defaultMessage="Confirm Password" />
              </label>
              <input
                placeholder="Enter confirm password"
                {...formik.getFieldProps('confirm_password')}
                className={clsx(
                  'form-control form-control-solid',
                  { 'is-invalid': formik.touched.confirm_password && formik.errors.confirm_password },
                  { 'is-valid': formik.touched.confirm_password && !formik.errors.confirm_password }
                )}
                type="password"
                disabled={formik.isSubmitting || isUserLoading}
              />
              {formik.touched.confirm_password && formik.errors.confirm_password && (
                <div className="fv-plugins-message-container">
                  <span role="alert">{formik.errors.confirm_password}</span>
                </div>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-lg-6 fv-row mb-7">
              <label className="required fw-bold fs-6 mb-2">
                <FormattedMessage id="USER.FORM.INPUT.ROLE" defaultMessage="Role" />
              </label>
              <select
                {...formik.getFieldProps('roles')}
                className="form-select form-control"
                disabled={formik.isSubmitting || isUserLoading}
              >
                <option value="">{"Choose Role"}</option>
                {role && role.map((user_role) => (
                  <option key={user_role.id} value={user_role.id}>
                    {user_role.name}
                  </option>
                ))}
              </select>
              {formik.touched.roles && formik.errors.roles && (
                <div className="fv-plugins-message-container">
                  <span role="alert">{""+formik.errors.roles}</span>
                </div>
              )}
            </div>


            {/* begin::Input group */}

            <div className="col-lg-6 fv-row mb-7"><label className="form-label required">
              <FormattedMessage id='COUNTRY.FORM.INPUT.CURRENCY' />
            </label>
              <select
                {...formik.getFieldProps('currency_preference_id')}
                className={clsx(
                  'form-select form-select-lg form-select-solid form-control form-control-solid mb-3 mb-lg-0',
                  { 'is-invalid': formik.touched.currency && formik.errors.currency },
                  {
                    'is-valid': formik.touched.currency && !formik.errors.currency,
                  }
                )}
                autoComplete='off'
                disabled={formik.isSubmitting || isUserLoading}

              ><option value='' defaultValue={""}>{intl.formatMessage({ id: 'FORM.GENERAL.SELECT' })}</option>
                {currencies && currencies.map(currency => (
                  <option value={currency.id} key={currency.id}>{currency.name}</option>
                ))}
              </select>
              {formik.touched.currency && formik.errors.currency && (
                <div className='fv-plugins-message-container'>
                  {/* <span role='alert'>{formik.errors.service_id}</span> */}
                </div>
              )}</div>
            {/* end::Input group */}
          </div>

        </div>

        <div className="text-center pt-15">
          <button
            type="reset"
            onClick={() => cancel()}
            className="btn btn-light me-3"
            disabled={formik.isSubmitting || isUserLoading}
          >
            <FormattedMessage id="FORM.GENERAL.DISCARD" defaultMessage="Discard" />
          </button>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={isUserLoading || formik.isSubmitting || !formik.isValid || !formik.touched}
          >
            <span className="indicator-label">
              <FormattedMessage id="FORM.GENERAL.SUBMIT" defaultMessage="Submit" />
            </span>
            {(formik.isSubmitting || isUserLoading) && (
              <span className="indicator-progress">
                <FormattedMessage id="FORM.GENERAL.PLEASEWAIT" defaultMessage="Please wait..." />
                {' '}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            )}
          </button>
        </div>
      </form>
      {(formik.isSubmitting || isUserLoading) && <UsersListLoading />}
    </>
  );
};

export { UserEditModalForm };
